<template>
<section class="login-wrapper">
  <!-- 背景光晕装饰 -->
  <div class="bg-glow bg-glow-1"></div>
  <div class="bg-glow bg-glow-2"></div>

  <div class="login-card">
    <!-- 顶部 Logo 区域 -->
    <div class="logo-area">
      <img
        src="@/assets/img/game-logo.png"
        class="login-logo-img"
        alt="Channel Admin Logo"
      />
      <h1 class="brand-title">Channel Admin</h1>
      <p class="brand-subtitle">游戏渠道管理后台</p>
    </div>

    <!-- 中间品牌标题与简短说明 -->
    <div class="welcome-area">
      <h2 class="welcome-title">欢迎回来</h2>
      <p class="welcome-desc">请登录以访问您的管理控制台</p>
    </div>

    <!-- 下方主登录按钮 -->
    <div class="action-area">
      <button class="btn-login" @click="login">
        <!-- 授权按钮使用指定的 SVG 结构，移除 rect 填充以统一颜色 -->
        <svg class="btn-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M24 14C18.4772 14 14 18.4772 14 24C14 29.5228 18.4772 34 24 34C29.5228 34 34 29.5228 34 24" stroke="white" stroke-width="4" stroke-linecap="round"></path>
          <circle cx="24" cy="24" r="4" fill="white"></circle>
        </svg>
        <span class="btn-text">统一认证登录</span>
      </button>
    </div>

    <!-- 无权限警告提示框 -->
    <transition name="fade">
      <div class="forbid-box" v-show="systemForbid">
        <div class="alert-content">
          <svg class="alert-warning-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <div class="alert-text">
            <span class="alert-title">未获得系统授权</span>
            <span class="alert-desc">{{ forbidMsg }}</span>
          </div>
        </div>
      </div>
    </transition>
  </div>

  <!-- 底部版权信息 -->
  <footer class="login-footer">
    <p>© 2026 Channel Admin. All Rights Reserved.</p>
  </footer>
</section>
</template>

<script>
import p from '@/common/privilege'

export default {
  data () {
    return {
      form: {},
      systemForbid: false,
      forbidMsg: '您没有此系统权限，请联系管理员！'
    }
  },
  created () {
    this.checkLogin()
  },
  methods: {
    checkLogin () {
      const query = this.$route.query
      if (!query) {
        return
      }
      if (Object.prototype.hasOwnProperty.call(query, 'f') && query.f === p.ForbidNonce) {
        this.systemForbid = true
        this.$message.error(this.forbidMsg)
        return
      }
      if (Object.prototype.hasOwnProperty.call(query, 'access_token')) {
        const accessToken = query.access_token
        const resValues = query.res_values
        if (!resValues || resValues === '[]' || !accessToken) {
          const redirectUri = encodeURIComponent(window.location.origin + '#/login?f=' + p.ForbidNonce)
          window.location.href = p.OAuthExitUrl + '?redirect_uri=' + redirectUri
        } else {
          this.$store.dispatch('ThirdLogin', accessToken).then(() => {
            this.$router.push({ path: '/' })
          })
        }
      }
    },
    login () {
      this.systemForbid = false
      const redirectUri = encodeURIComponent(window.location.origin + '#/login?')
      window.location.href = p.OAuthUrl + '?client_id=channel&response_type=token&redirect_uri=' + redirectUri
    }
  }
}
</script>

<style scoped>
/* 全局页面包裹器 */
.login-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100vw;
  background-color: var(--bg-page);
  position: relative;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  box-sizing: border-box;
}

/* 柔和背景光晕 */
.bg-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  z-index: 1;
  pointer-events: none;
  opacity: 0.6;
}
.bg-glow-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0) 70%);
  top: -10%;
  left: -10%;
}
.bg-glow-2 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, rgba(99, 102, 241, 0) 70%);
  bottom: -15%;
  right: -10%;
}

/* 登录卡片 (进一步调大整体尺寸，增强呼吸感) */
.login-card {
  width: 100%;
  max-width: 480px; /* 从 440px 调大至 480px */
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 56px 48px; /* 增大内边距 */
  box-shadow: 0 12px 30px -5px rgba(0, 0, 0, 0.02),
              0 24px 48px -15px rgba(0, 0, 0, 0.04);
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.login-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 36px -5px rgba(0, 0, 0, 0.03),
              0 28px 56px -15px rgba(0, 0, 0, 0.05);
}

/* Logo 区域 */
.logo-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
}
.login-logo-img {
  height: 80px;
  max-width: 100%;
  object-fit: contain;
  margin-bottom: 12px;
  transition: background-color 0.2s ease;
}
html.dark .login-logo-img {
  background-color: rgba(255, 255, 255, 0.85);
  border-radius: 8px;
  padding: 4px;
  box-sizing: border-box;
}
.brand-title {
  font-size: 28px;
  font-weight: 800;
  color: var(--text-main);
  margin: 0;
  letter-spacing: 0.5px;
}
.brand-subtitle {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted);
  margin: 6px 0 0 0;
  letter-spacing: 1.5px;
}

/* 欢迎文案区域 */
.welcome-area {
  text-align: center;
  margin-bottom: 36px;
  width: 100%;
}
.welcome-title {
  font-size: 22px; /* 从 20px 调大至 22px */
  font-weight: 600;
  color: var(--text-main);
  margin: 0;
}
.welcome-desc {
  font-size: 15px; /* 从 14px 调大至 15px */
  color: var(--text-muted);
  margin: 10px 0 0 0;
}

/* 登录按钮区域 */
.action-area {
  width: 100%;
}
.btn-login {
  width: 100%;
  height: 52px; /* 从 48px 调大至 52px */
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  border: none;
  border-radius: 14px;
  font-size: 16px; /* 从 15px 调大至 16px */
  font-weight: 600;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.2);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
  outline: none;
}

.btn-login:hover {
  background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
  box-shadow: 0 6px 18px rgba(37, 99, 235, 0.28);
  transform: translateY(-1px);
}

.btn-login:active {
  transform: translateY(1px);
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.2);
}

.btn-icon {
  width: 24px;
  height: 24px;
  margin-right: 4px;
  flex-shrink: 0;
  display: block;
  position: relative;
  top: -0.5px; /* 精确像素级向上位移，保证视觉中轴对齐 */
}

.btn-text {
  display: inline-flex;
  align-items: center;
  height: 24px; /* 与图标高度完全一致 */
  line-height: 24px;
}

/* 警告提示组件 */
.forbid-box {
  width: 100%;
  margin-top: 20px;
}
.alert-content {
  display: flex;
  align-items: flex-start;
  background-color: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 12px;
  padding: 14px 16px;
}
.alert-warning-icon {
  color: #ef4444;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  margin-right: 12px;
  flex-shrink: 0;
}
.alert-text {
  display: flex;
  flex-direction: column;
}
.alert-title {
  font-size: 13px;
  font-weight: 600;
  color: #991b1b;
  margin-bottom: 2px;
}
.alert-desc {
  font-size: 12px;
  color: #ef4444;
  line-height: 1.4;
}

/* 底部版权信息 */
.login-footer {
  margin-top: 28px;
  z-index: 10;
  text-align: center;
}
.login-footer p {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
  letter-spacing: 0.3px;
}

/* 警告框动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* 响应式适配 */
@media (max-width: 480px) {
  .login-card {
    border-radius: 0;
    border: none;
    box-shadow: none;
    padding: 32px 24px;
    height: 100vh;
    justify-content: center;
  }
  .login-wrapper {
    background-color: #ffffff;
  }
  .bg-glow {
    display: none;
  }
  .login-footer {
    position: absolute;
    bottom: 24px;
  }
}
</style>
